import React from 'react';

// Top-level domains accepted for a BARE domain — one written with no scheme
// and no www., e.g. "connectionpointapp.com" or "pamperedchef.com/pws/name".
// Deliberately tiny, and deliberately not the set the scheme and www. forms
// accept.
//
// The list is short because consultants text without a space after a full
// stop, and every entry here is a word that could start the next sentence.
// Measured over 90 days of the support inbox, among URL-shaped strings the old
// pattern missed: 16 were .com, one .net, one .info — and more than twenty were
// sentence boundaries. ".and", ".but", ".how", ".because", ".please", ".now",
// ".only", ".is", ".send". A wider list does not find more links; it finds
// those.
//
// So this is the classic five, none of which is an English word. Two-letter
// TLDs are out for the same reason (".us", ".co", ".it", ".me"), and so are
// ordinary nouns that happen to be TLDs — .news, .life, .link, .page, .live,
// .store, .shop, .blog. None of them appeared in a real link in that window.
// A scheme or a www. is an explicit statement of intent, so those forms stay
// permissive; a bare domain is a guess, so it has to earn it.
const BARE_TLDS = [
  'com', 'net', 'org', 'edu', 'gov',
].join('|');

// Matched FIRST so an address is consumed whole and never reaches the link
// branch. Without this the bare-domain rule turns "kara@gmail.com" into
// "kara@" followed by a link to gmail.com — which is how a one-line "just
// linkify bare domains" change quietly breaks 115 messages a quarter.
const EMAIL_SOURCE = '[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}';

const URL_SOURCE = [
  EMAIL_SOURCE,
  // Explicit scheme: intent is unambiguous, so take everything up to
  // whitespace. Trailing punctuation is trimmed off the href below.
  'https?://\\S+',
  // No scheme but an explicit www.
  'www\\.\\S+',
  // Bare domain, conservative TLD, optional path/query/fragment.
  `(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\\.)+(?:${BARE_TLDS})\\b(?:[/?#]\\S*)?`,
].join('|');

const isEmail = (value) => new RegExp(`^${EMAIL_SOURCE}$`, 'i').test(value);

/**
 * linkifyText
 *
 * Turns URLs inside plain message text into anchors. Used for BOTH directions
 * in the chat transcript (Message.jsx), so a change here affects what a
 * consultant sent as well as what they received.
 *
 * Before CON-1673 the pattern required http://, https:// or www., so a bare
 * domain rendered as unclickable text. Measured over 90 days of the support
 * inbox: 226 inbound messages carried a real URL and about 9 of them were
 * scheme-less — a personal-website link, a party link, a CP domain. Small, but
 * they were exactly the links someone needed to open.
 */
export function linkifyText(textToLinkify) {
  const text = String(textToLinkify || '').trim();
  if (!text) return <>&nbsp;</>;

  // Rebuilt per call: a /g regex carries lastIndex, and split() plus the
  // per-part test below both consume it. A shared instance skips matches.
  const urlRegEx = new RegExp(`(${URL_SOURCE})`, 'gi');

  const parts = text.split(urlRegEx);

  return (
    <>
      {parts.map((part, index) => {
        // An email address is matched only so it can be excluded here; it is
        // still rendered, just as text.
        if (part && !isEmail(part) && new RegExp(`^(?:${URL_SOURCE})$`, 'i').test(part)) {
          const key = index;
          const cleanLink = part.replace(/[),.;'"\]]+$/g, '');
          const href = cleanLink.toLowerCase().startsWith('http')
            ? cleanLink
            : `https://${cleanLink}`;

          return (
            <a
              key={key}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {part}
            </a>
          );
        }
        return part;
      })}
    </>
  );
}
